import XRegExp from "xregexp";
import { SupportedLanguages, TextTemplateData, TextTemplateKey, TextTemplatesByLanguage } from "../components";

/**
 *
 * @param template A very simple template processor function that replaces token like `{key}` with a `value`
 * when passing the `data` object as `{key: "value"}`.
 * @param data Object to use for template population.
 */
export const populateTemplate = (template: string, data: { [K: string]: string } = {}) =>
  Object.entries(data).reduce((template, [key, value]) => template.replace(XRegExp(`{${key}}`, "g"), value), template);

/**
 * Get a localized string of text by populating a text template from application
 * settings against the supplied template key
 *
 * @param language The language for localized string to be returned
 * @param textTemplatesByLanguage Mapping object of localized text templates for all supported languages
 * @param textTemplateKey The unique identifier to use to look up a text template
 * @param textTemplateData Optional data to be populated in the text template
 * @param defaultString Optional default string to use if template not found
 * @returns The text template against the provided key + language combination, populated with the data if provided.
 * OR the default string, if template not found. OR the text template key itself, if template not found and default
 * not provided.
 */
export const getLocalizedString = <T extends TextTemplateKey>(
  language: SupportedLanguages,
  textTemplatesByLanguage: TextTemplatesByLanguage<T>,
  textTemplateKey: T,
  textTemplateData?: TextTemplateData,
  defaultString?: string
) => {
  const textTemplate = textTemplatesByLanguage[language]?.[textTemplateKey];

  if (!textTemplate) {
    return defaultString ?? textTemplateKey;
  }

  const populatedTextTemplate = populateTemplate(textTemplate, textTemplateData);

  return populatedTextTemplate;
};

/**
 * A utility function to make a scoped text template key string given a list of scopes.
 * It prefixes the list of scopes to the text template key in reverse order, delimited by the underscore (_) character.
 *
 * @param textTemplateKey The text template key to be scoped
 * @param scopes An optional list of scopes
 * @returns The text template key prefixed with the list of scopes in reverse order
 */
export const makeScopedTextTemplateKey = <T extends TextTemplateKey>(textTemplateKey: T, scopes: string[] = []): T => {
  const filteredScopes = scopes.filter(Boolean);
  const reversedScopes = filteredScopes.reverse();
  reversedScopes.push(textTemplateKey);
  return reversedScopes.join("_") as T;
};
